/**
 * versionPinger.js — Auto-reload stale tabs when a new build lands.
 *
 * How it works
 * ------------
 * 1. On first run, we read the currently-loaded main webpack chunk filename
 *    from the document's <script src="/static/js/main.*.js"> tag. That hash
 *    changes on every deploy (CRA appends a content hash to the filename).
 * 2. Every `POLL_MS` (default 60 s), while the tab is visible, we fetch
 *    `/index.html` with `cache: 'no-store'` and parse out the new main
 *    chunk filename.
 * 3. If the live filename differs from the one we loaded with, a new build
 *    has shipped → we do a one-off hard reload (sessionStorage guard avoids
 *    infinite loops).
 *
 * Why not the service worker's updatefound event?
 * -----------------------------------------------
 * We also rely on that — it surfaces the `credys:update-available` toast
 * for users who have the SW installed. But not every tab has an active SW
 * (first visit, incognito, SW still installing), so a lightweight polling
 * fallback guarantees no tab is left behind.
 *
 * No backend changes required.
 */

const POLL_MS = 60_000;          // how often to check
const STORAGE_KEY = '__credys_version_reloaded__';
const MAIN_CHUNK_RE = /\/static\/js\/main\.[\w]+\.js/;

const readMainChunkFromHtml = (html) => {
  const m = html.match(MAIN_CHUNK_RE);
  return m ? m[0] : null;
};

const readMainChunkFromDocument = () => {
  const scripts = Array.from(document.querySelectorAll('script[src]'));
  for (const s of scripts) {
    const src = s.getAttribute('src') || '';
    const m = src.match(MAIN_CHUNK_RE);
    if (m) return m[0];
  }
  return null;
};

const hardReload = () => {
  if (sessionStorage.getItem(STORAGE_KEY)) return; // already reloaded this session
  sessionStorage.setItem(STORAGE_KEY, '1');
  // Clear all caches + unregister any SW so the reload truly fetches fresh.
  (async () => {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch (_) { /* ignore */ }
    window.location.reload();
  })();
};

const checkOnce = async (loadedChunk) => {
  try {
    const res = await fetch('/index.html', { cache: 'no-store' });
    if (!res.ok) return;
    const html = await res.text();
    const liveChunk = readMainChunkFromHtml(html);
    if (liveChunk && liveChunk !== loadedChunk) {
      // eslint-disable-next-line no-console
      console.info('[versionPinger] New build detected — hard reloading.', {
        loaded: loadedChunk,
        live: liveChunk,
      });
      hardReload();
    }
  } catch (_) {
    // Network error — ignore, we'll try again next tick
  }
};

export const startVersionPinger = () => {
  // Clear the one-shot guard once we've been running cleanly for a minute,
  // so a future deploy can trigger another recovery.
  setTimeout(() => sessionStorage.removeItem(STORAGE_KEY), 60_000);

  const loadedChunk = readMainChunkFromDocument();
  if (!loadedChunk) return; // dev server / unknown bundler — skip

  let timer = null;
  const tick = () => checkOnce(loadedChunk);

  const start = () => {
    if (timer) return;
    timer = setInterval(tick, POLL_MS);
  };
  const stop = () => {
    if (!timer) return;
    clearInterval(timer);
    timer = null;
  };

  // Kick off 30 s after load so we never collide with the initial
  // hydration / service-worker registration.
  setTimeout(() => {
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        // Immediate check on tab-focus — captures deploys that landed while
        // the tab was backgrounded, before the next poll tick.
        tick();
        start();
      } else {
        stop();
      }
    });
    // Also check on window focus (covers browsers where visibilitychange
    // fires slightly differently — cheap to be defensive).
    window.addEventListener('focus', tick);
  }, 30_000);
};

export default startVersionPinger;
